import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Package, Tag, Save, AlertCircle, CheckCircle, Edit } from 'lucide-react';
import { toast } from 'react-toastify';
import { productosApi, variablesProductosApi, Producto, DefinicionProducto } from '../../apis/productosDinamicosApi';
import { getAllTags } from '../../apis/blacApi';
import { TagBlac } from '../../interfaces/TagBlacInterface';

interface EditarProductoProps {
  producto: Producto;
  onClose: () => void;
  onSuccess: () => void;
}

const EditarProducto: React.FC<EditarProductoProps> = ({ producto, onClose, onSuccess }) => {
  const [definicionProducto, setDefinicionProducto] = useState<DefinicionProducto | null>(null);
  const [tagsBlac, setTagsBlac] = useState<TagBlac[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [formData, setFormData] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState('');

  const { register, handleSubmit, formState: { errors }, setValue } = useForm();

  useEffect(() => {
    loadDefinicionProducto();
    loadTagsBlac();
  }, []);

  useEffect(() => {
    if (definicionProducto && producto.datos) {
      // Mapear los datos existentes del producto
      const initialData: { [key: string]: string } = {};
      definicionProducto.variables.forEach((variable, index) => {
        const fieldName = `variable_${index}`;
        const value = producto.datos[index] || '';
        initialData[fieldName] = value;
        setValue(fieldName, value);
      });
      setFormData(initialData);
      
      // Establecer el Tag seleccionado
      setValue('tagblac_id', producto.tagblac_id || producto.tag_tracksolid_id || '');
    }
  }, [definicionProducto, producto, setValue]);

  const loadDefinicionProducto = async () => {
    try {
      const definicion = await variablesProductosApi.getDefinicion(producto.variable_producto_id);
      setDefinicionProducto(definicion);
    } catch (error) {
      console.error('Error cargando definición del producto:', error);
      toast.error('Error al cargar la definición del producto');
    }
  };

  const loadTagsBlac = async () => {
    try {
      setLoadingTags(true);
      const tags = await productosApi.getTagsDisponibles();
      
      // Si el producto ya tiene una tag asignada, incluirla en la lista
      const currentTag = producto.tag_blac || producto.tag_tracksolid;
      if (currentTag && !tags.find((tag: any) => tag.id === currentTag.id)) {
        tags.unshift(currentTag);
      }
      
      setTagsBlac(tags);
    } catch (error) {
      console.error('Error cargando tags BLAC:', error);
      toast.error('Error al cargar las tags BLAC disponibles');
    } finally {
      setLoadingTags(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (!definicionProducto) {
      toast.error('Error: No se pudo cargar la definición del producto');
      return;
    }

    try {
      setLoading(true);
      setSuccessMessage('');

      // Construir array de datos en el orden correcto
      const datos: string[] = [];
      definicionProducto.variables.forEach((variable, index) => {
        datos.push(data[`variable_${index}`] || '');
      });

      const updateData = {
        tagblac_id: data.tagblac_id ? parseInt(data.tagblac_id) : undefined,
        datos
      };

      await productosApi.actualizar(producto.id, updateData);
      
      setSuccessMessage('Producto actualizado exitosamente');
      
      // Cerrar modal después de 1.5 segundos
      setTimeout(() => {
        onSuccess();
      }, 1500);

    } catch (error: any) {
      console.error('Error actualizando producto:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setValue(field, value);
  };

  const styles = `
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    }

    .modal-container {
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      width: 100%;
      max-width: 800px;
      max-height: 90vh;
      overflow-y: auto;
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 2rem 2rem 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .modal-title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .product-id {
      background: rgba(0, 122, 255, 0.1);
      color: #007AFF;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 500;
      margin-left: 0.5rem;
    }

    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .close-button:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }

    .modal-content {
      padding: 2rem;
    }

    .success-message {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.5rem;
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.3);
      border-radius: 12px;
      color: #10B981;
      margin-bottom: 1.5rem;
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .product-info {
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 12px;
      padding: 1rem 1.5rem;
      margin-bottom: 2rem;
    }

    .product-info-title {
      font-weight: 600;
      color: #007AFF;
      margin-bottom: 0.5rem;
    }

    .product-info-details {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
    }

    .form-section {
      margin-bottom: 2rem;
    }

    .section-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.5rem;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-label {
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.9rem;
    }

    .form-label.required::after {
      content: ' *';
      color: #EF4444;
    }

    .form-input, .form-select, .form-textarea {
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .form-input:focus, .form-select:focus, .form-textarea:focus {
      outline: none;
      background: rgba(255, 255, 255, 0.15);
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .form-input::placeholder, .form-textarea::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .form-select option {
      background: #1a1a1a;
      color: white;
    }

    .form-textarea {
      min-height: 100px;
      resize: vertical;
    }

    .error-message {
      color: #EF4444;
      font-size: 0.8rem;
      margin-top: 0.25rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .loading-tags {
      color: rgba(255, 255, 255, 0.6);
      font-style: italic;
      font-size: 0.9rem;
    }

    .current-tag {
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.3);
      border-radius: 8px;
      padding: 0.75rem;
      margin-bottom: 1rem;
    }

    .current-tag-title {
      font-weight: 500;
      color: #10B981;
      margin-bottom: 0.25rem;
    }

    .current-tag-info {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding: 1.5rem 2rem 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      font-weight: 500;
      font-size: 14px;
      transition: all 0.3s ease;
      cursor: pointer;
      border: none;
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn-primary {
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(0, 122, 255, 0.3);
    }

    .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 122, 255, 0.4);
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid white;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .modal-container {
        margin: 1rem;
        max-height: calc(100vh - 2rem);
      }

      .modal-header, .modal-content, .modal-footer {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }

      .form-grid {
        grid-template-columns: 1fr;
      }

      .modal-footer {
        flex-direction: column;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2 className="modal-title">
              <Edit size={24} />
              Editar Producto
              <span className="product-id">#{producto.id}</span>
            </h2>
            <button onClick={onClose} className="close-button">
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="modal-content">
            {successMessage && (
              <div className="success-message">
                <CheckCircle size={20} />
                {successMessage}
              </div>
            )}

            {/* Información del Producto */}
            <div className="product-info">
              <div className="product-info-title">Información del Producto</div>
              <div className="product-info-details">
                <strong>Tipo:</strong> {producto.tipo_producto} | 
                <strong> Creado:</strong> {new Date(producto.created_at).toLocaleDateString('es-MX')} |
                <strong> Estado:</strong> {producto.activo ? 'Activo' : 'Inactivo'}
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Variables del Producto */}
              {definicionProducto && (
                <div className="form-section">
                  <h3 className="section-title">
                    <Package size={18} />
                    Datos del Producto
                  </h3>
                  <div className="form-grid">
                    {definicionProducto.variables.map((variable, index) => (
                      <div key={index} className="form-group">
                        <label className="form-label required">
                          {variable.charAt(0).toUpperCase() + variable.slice(1).replace(/_/g, ' ')}
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder={`Ingrese ${variable.replace(/_/g, ' ')}`}
                          {...register(`variable_${index}`, { required: `${variable} es requerido` })}
                          onChange={(e) => handleInputChange(`variable_${index}`, e.target.value)}
                        />
                        {errors[`variable_${index}`] && (
                          <span className="error-message">
                            <AlertCircle size={14} />
                            {errors[`variable_${index}`]?.message as string}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tag */}
              <div className="form-section">
                <h3 className="section-title">
                  <Tag size={18} />
                  Tag
                </h3>
                
                {(producto.tag_blac || producto.tag_tracksolid) && (
                  <div className="current-tag">
                    <div className="current-tag-title">Tag Actual</div>
                    <div className="current-tag-info">
                      {producto.tag_blac?.name_device || producto.tag_tracksolid?.device_name} - {producto.tag_blac?.plate || producto.tag_tracksolid?.plate}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Cambiar Tag (Opcional)</label>
                  {loadingTags ? (
                    <div className="loading-tags">Cargando tags disponibles...</div>
                  ) : (
                    <select
                      className="form-select"
                      {...register('tagblac_id')}
                    >
                      <option value="">Sin tag asignada</option>
                      {tagsBlac.map(tag => (
                        <option key={tag.id} value={tag.id}>
                          {tag.name_device} - {tag.plate}
                          {tag.id === producto.tagblac_id ? ' (Actual)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              className="btn btn-primary"
              disabled={loading || !definicionProducto}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Actualizando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Actualizar Producto
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditarProducto;
